import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const PARAM = "support";
const VALUE = "chat";

// Intercom is booted by GTM, not by this app, so it can arrive long after
// React has mounted — on a cold load the tag is still being fetched when the
// first route renders. Poll instead of racing it.
const POLL_MS = 150;
// Past this the tag is not coming (blocked by an ad blocker, GTM container not
// published for the environment); stop burning timers.
const READY_TIMEOUT_MS = 20000;
// `booted` is set by the real Intercom bundle once the Messenger is up. The
// snippet that GTM injects defines window.Intercom as a queueing stub before
// that, and a showNewMessage on the stub is only replayed if a boot follows.
// If the flag never shows up but the function does, fall back to the queue
// rather than dropping the request entirely.
const BOOTED_FLAG_TIMEOUT_MS = 5000;

function isBooted() {
  return typeof window.Intercom === "function" && window.Intercom.booted === true;
}

function isQueueable() {
  return typeof window.Intercom === "function";
}

/**
 * Opens the Intercom Messenger on the new-message composer when the current
 * URL carries `?support=chat`, so an email CTA can link straight into support
 * on any route (/dashboard?support=chat, /setup?support=chat, …).
 *
 * Mounted once, globally, above <Routes>. Two things keep it from firing more
 * than once: the param is stripped from the URL as soon as the Messenger is
 * asked to open, and every wait is cancelled when the location changes.
 *
 * That cancellation is also what makes the logged-out path work. An
 * unauthenticated hit on /dashboard?support=chat renders ProtectedRoute, which
 * redirects to /login without the query string — this hook's pending wait is
 * dropped there and picked up again after login, when ProtectedRoute's
 * remembered `from` sends the user back to /dashboard?support=chat.
 */
export function useSupportChatDeepLink() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(PARAM) !== VALUE) return;

    let cancelled = false;
    let timer;
    const startedAt = Date.now();

    const open = () => {
      // Strip the param first: this effect re-runs on the resulting location
      // change and must not find the deep link a second time. It also stops a
      // manual refresh from reopening the composer.
      params.delete(PARAM);
      const search = params.toString();
      navigate(`${location.pathname}${search ? `?${search}` : ""}${location.hash}`, {
        replace: true,
      });

      window.Intercom("showNewMessage");
    };

    const tick = () => {
      if (cancelled) return;

      const waited = Date.now() - startedAt;

      if (isBooted() || (waited >= BOOTED_FLAG_TIMEOUT_MS && isQueueable())) {
        open();
        return;
      }

      if (waited >= READY_TIMEOUT_MS) return;

      timer = setTimeout(tick, POLL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [location.pathname, location.search, location.hash, navigate]);
}
